import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import Sortable from 'sortablejs';
import { GLOBALS } from './globals';
import { SortablejsService } from './sortablejs.service';

// ---------- helper classes (not exported) ----------

class SortablejsBinding {
  constructor(private target: any) {}

  insert(index: number, item: any): void {
    if (this.isFormArray) {
      this.target.insert(index, item);
    } else {
      this.target.splice(index, 0, item);
    }
  }

  get(index: number): any {
    return this.isFormArray ? this.target.at(index) : this.target[index];
  }

  remove(index: number): any {
    let item: any;
    if (this.isFormArray) {
      item = this.target.at(index);
      this.target.removeAt(index);
    } else {
      item = this.target.splice(index, 1)[0];
    }
    return item;
  }

  // we need this to identify that the target is a FormArray
  // we don't want to have a dependency on @angular/forms just for that
  private get isFormArray(): boolean {
    // just checking for random FormArray methods not available on a standard array
    return !!this.target.at && !!this.target.insert && !!this.target.reset;
  }
}

class SortablejsBindings {
  bindings: SortablejsBinding[];

  constructor(bindingTargets: any[]) {
    this.bindings = bindingTargets.map(target => new SortablejsBinding(target));
  }

  injectIntoEvery(index: number, items: any[]): void {
    this.bindings.forEach((b, i) => b.insert(index, items[i]));
  }

  getFromEvery(index: number): any[] {
    return this.bindings.map(b => b.get(index));
  }

  extractFromEvery(index: number): any[] {
    return this.bindings.map(b => b.remove(index));
  }

  get provided(): boolean {
    return !!this.bindings.length;
  }
}

// ---------- helper function ----------

interface SortableEvent {
  oldIndex: number;
  newIndex: number;
  oldDraggableIndex?: number;
  newDraggableIndex?: number;
  item: HTMLElement;
  clone: HTMLElement;
}

const getIndexesFromEvent = (event: SortableEvent): { new: number; old: number } => {
  if (event.hasOwnProperty('newDraggableIndex') && event.hasOwnProperty('oldDraggableIndex')) {
    return {
      new: event.newDraggableIndex!,
      old: event.oldDraggableIndex!,
    };
  } else {
    return {
      new: event.newIndex,
      old: event.oldIndex,
    };
  }
};

// ---------- directive ----------

@Directive({
  standalone: false,
  selector: '[sortablejs]',
})
export class SortablejsDirective implements OnInit, OnChanges, OnDestroy {
  @Input() sortablejs: any;
  @Input() sortablejsContainer: string;
  @Input() sortablejsOptions: any;
  @Input() sortablejsCloneFunction: (item: any) => any;
  @Output() sortablejsInit = new EventEmitter<any>();

  private sortableInstance: any;

  constructor(
    @Optional() @Inject(GLOBALS) private globalConfig: any,
    private service: SortablejsService,
    private element: ElementRef,
    private zone: NgZone,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    if (Sortable && Sortable.create) {
      // Sortable does not exist in angular universal (SSR)
      this.create();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const optionsChange = changes['sortablejsOptions'];
    if (optionsChange && !optionsChange.isFirstChange()) {
      const previousOptions = optionsChange.previousValue;
      const currentOptions = optionsChange.currentValue;
      Object.keys(currentOptions).forEach(optionName => {
        if (currentOptions[optionName] !== previousOptions[optionName]) {
          // use low-level option setter
          this.sortableInstance.option(optionName, this.options[optionName]);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.sortableInstance) {
      this.sortableInstance.destroy();
    }
  }

  private create(): void {
    const container = this.sortablejsContainer
      ? this.element.nativeElement.querySelector(this.sortablejsContainer)
      : this.element.nativeElement;

    setTimeout(() => {
      this.zone.runOutsideAngular(() => {
        this.sortableInstance = Sortable.create(container, this.options);
      });
      this.sortablejsInit.emit(this.sortableInstance);
    }, 0);
  }

  private getBindings(): SortablejsBindings {
    if (!this.sortablejs) {
      return new SortablejsBindings([]);
    } else if (this.sortablejs instanceof SortablejsBindings) {
      return this.sortablejs;
    } else {
      return new SortablejsBindings([this.sortablejs]);
    }
  }

  private get options(): any {
    return { ...this.optionsWithoutEvents, ...this.overridenOptions };
  }

  private get optionsWithoutEvents(): any {
    return { ...(this.globalConfig || {}), ...(this.sortablejsOptions || {}) };
  }

  private proxyEvent(eventName: string, ...params: any[]): void {
    this.zone.run(() => {
      if (this.optionsWithoutEvents && this.optionsWithoutEvents[eventName]) {
        this.optionsWithoutEvents[eventName](...params);
      }
    });
  }

  private get isCloning(): boolean {
    return this.sortableInstance.options.group.checkPull(this.sortableInstance, this.sortableInstance) === 'clone';
  }

  private clone<T>(item: T): T {
    // by default pass the item through, no cloning performed
    return (this.sortablejsCloneFunction || ((subitem: any) => subitem))(item);
  }

  private get overridenOptions(): any {
    // always intercept standard events but act only in case items are set (bindingEnabled)
    // allows to forget about tracking this.items changes
    return {
      onAdd: (event: any) => {
        this.service.transfer = (items: any[]) => {
          this.getBindings().injectIntoEvery(event.newIndex, items);
          this.proxyEvent('onAdd', event);
        };
        this.proxyEvent('onAddOriginal', event);
      },
      onRemove: (event: any) => {
        const bindings = this.getBindings();
        if (bindings.provided) {
          if (this.isCloning) {
            this.service.transfer!(bindings.getFromEvery(event.oldIndex).map(item => this.clone(item)));
            // great thanks to https://github.com/tauu
            // event.item is the original item from the source list which is moved to the target list
            // event.clone is a clone of the original item and will be added to source list
            // If bindings are provided, adding the item dom element to the target list causes artifacts
            // as it interferes with the rendering performed by the angular template.
            // Therefore we remove it immediately and also move the original item back to the source list.
            // (event handler may be attached to the original item and not its clone, therefore keeping
            // the original dom node, circumvents side effects )
            this.renderer.removeChild(event.item.parentNode, event.item);
            this.renderer.insertBefore(event.clone.parentNode, event.item, event.clone);
            this.renderer.removeChild(event.clone.parentNode, event.clone);
          } else {
            this.service.transfer!(bindings.extractFromEvery(event.oldIndex));
          }
          this.service.transfer = null;
        }
        this.proxyEvent('onRemove', event);
      },
      onUpdate: (event: any) => {
        const bindings = this.getBindings();
        const indexes = getIndexesFromEvent(event);
        bindings.injectIntoEvery(indexes.new, bindings.extractFromEvery(indexes.old));
        this.proxyEvent('onUpdate', event);
      },
    };
  }
}
